// ABOUTME: News article builder modal with split layout.
// ABOUTME: Header image area on top, split body (article left, audience right), footer with save/publish.

import { Box, IconButton, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { tokens } from "../../../../theme";
import SHModalBottom from "../../../theme/Modal/SHModalBottom";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../theme/buttons/Shbutton";
import ArticleBuilderView from "./views/ArticleBuilderView";
import AudiencePanelView from "./views/AudiencePanelView";
import {
    useAddNewsArticleMutation,
    useGetNewsArticleQuery,
    usePublishNewsArticleMutation,
    useUpdateNewsArticleMutation,
} from "../../../../newapi/global/newsRootSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../utils/toasts";

const hydrateSections = (raw) => (raw ?? []).map((s, idx) => ({
    ...s,
    id: s.id ?? Date.now() + idx,
}));

const buildPayloadAndFiles = ({ articleId, headerImage, title, sections, audience, excludedCustomerIds, excludedRelationCustomerIds, status }) => {
    const files = {};
    let newCounter = 0;

    let payloadHeader;
    if (headerImage) {
        payloadHeader = {};
        if (headerImage.imageId) payloadHeader.imageId = headerImage.imageId;
        if (headerImage.file) {
            payloadHeader.fileKey = "header";
            files.header = headerImage.file;
        }
    }

    const payloadSections = sections.map((s) => {
        if (s.type === "image") {
            const content = {};
            if (s.content?.imageId) content.imageId = s.content.imageId;
            if (s.content?.file) {
                const fileKey = s.content.imageId || `new_${newCounter++}`;
                content.fileKey = fileKey;
                files[fileKey] = s.content.file;
            }
            return { type: "image", content };
        }
        return { type: "text", content: s.content };
    });

    const payload = {
        articleType: "news",
        title,
        audience,
        excludedCustomerIds,
        excludedRelationCustomerIds,
        status,
        headerImage: payloadHeader,
        sections: payloadSections,
    };

    if (articleId) payload.id = articleId;

    return { payload, files };
};

const MCreateNewsArticle = ({ open, handleClose, articleId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [headerImage, setHeaderImage] = useState(null);
    const [title, setTitle] = useState("");
    const [sections, setSections] = useState([]);
    const [audience, setAudience] = useState([]);
    const [excludedCustomerIds, setExcludedCustomerIds] = useState([]);
    const [excludedRelationCustomerIds, setExcludedRelationCustomerIds] = useState([]);
    const [saving, setSaving] = useState(false);

    const { data: existingArticle } = useGetNewsArticleQuery(articleId, {
        skip: !open || !articleId,
    });

    const [addArticle] = useAddNewsArticleMutation();
    const [updateArticle] = useUpdateNewsArticleMutation();
    const [publishArticle] = usePublishNewsArticleMutation();

    useEffect(() => {
        if (open && !articleId) {
            setHeaderImage(null);
            setTitle("");
            setSections([]);
            setAudience([]);
            setExcludedCustomerIds([]);
            setExcludedRelationCustomerIds([]);
        }
    }, [open, articleId]);

    useEffect(() => {
        if (open && articleId && existingArticle) {
            setHeaderImage(existingArticle.headerImage ?? null);
            setTitle(existingArticle.title ?? "");
            setSections(hydrateSections(existingArticle.sections));
            setAudience(existingArticle.audience ?? []);
            setExcludedCustomerIds(existingArticle.excludedCustomerIds ?? []);
            setExcludedRelationCustomerIds(existingArticle.excludedRelationCustomerIds ?? []);
        }
    }, [open, articleId, existingArticle]);

    let isStandAlone = false;
    if (window.matchMedia("(display-mode: standalone)").matches) {
        isStandAlone = true;
    }

    const handleHeaderImageUpload = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            if (headerImage?.previewUrl) {
                URL.revokeObjectURL(headerImage.previewUrl);
            }
            const previewUrl = URL.createObjectURL(file);
            setHeaderImage({
                ...(headerImage?.imageId ? { imageId: headerImage.imageId } : {}),
                file,
                previewUrl,
            });
        };
        input.click();
    }, [headerImage]);

    const handleRemoveHeaderImage = useCallback(() => {
        if (headerImage?.previewUrl) {
            URL.revokeObjectURL(headerImage.previewUrl);
        }
        setHeaderImage(null);
    }, [headerImage]);

    const persistArticle = async (status) => {
        const args = buildPayloadAndFiles({
            articleId,
            headerImage,
            title,
            sections,
            audience,
            excludedCustomerIds,
            excludedRelationCustomerIds,
            status,
        });

        if (articleId) {
            return updateArticle({ id: articleId, ...args }).unwrap();
        }
        return addArticle(args).unwrap();
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await persistArticle("draft");
            showToastMessage("success", t("article_saved") || "Artikel opgeslagen");
            handleClose?.();
        } catch (err) {
            showToastMessageRequestError(t("save_failed") || "Opslaan mislukt", t, err);
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        if (saving) return;
        if (!audience || audience.length === 0) return;
        setSaving(true);
        try {
            const saved = await persistArticle("draft");
            const idToPublish = saved?.id ?? articleId;
            if (idToPublish) {
                await publishArticle(idToPublish).unwrap();
            }
            showToastMessage("success", t("article_published") || "Artikel gepubliceerd");
            handleClose?.();
        } catch (err) {
            showToastMessageRequestError(t("publish_failed") || "Publiceren mislukt", t, err);
        } finally {
            setSaving(false);
        }
    };

    const publishDisabled = saving || !audience || audience.length === 0;

    return (
        <SHModalBottom
            open={open}
            onClose={handleClose}
        >
            {/* HEADER — Image upload area, sits on tertiary bg like MCreateBatch header */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {(headerImage?.url || headerImage?.previewUrl) ? (
                    <>
                        <Box
                            component="img"
                            src={headerImage.url || headerImage.previewUrl}
                            alt=""
                            sx={{
                                width: "100%",
                                height: 220,
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                        <Box
                            className="header-overlay"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.25)",
                                opacity: 0,
                                transition: "opacity 150ms",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                "&:hover": { opacity: 1 },
                            }}
                        >
                            <IconButton
                                onClick={handleHeaderImageUpload}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    background: "rgba(255,255,255,0.9)",
                                    color: colors.txt["primary"],
                                    "&:hover": { background: "#fff" },
                                }}
                            >
                                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                            <IconButton
                                onClick={handleRemoveHeaderImage}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    background: "rgba(255,255,255,0.9)",
                                    color: colors.red[400],
                                    "&:hover": { background: "#fff" },
                                }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Box>
                    </>
                ) : (
                    <Box
                        onClick={handleHeaderImageUpload}
                        sx={{
                            width: "100%",
                            height: 180,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            cursor: "pointer",
                            transition: "150ms",
                            "&:hover": {
                                opacity: 0.7,
                            },
                        }}
                    >
                        <AddPhotoAlternateOutlinedIcon
                            sx={{ fontSize: 40, color: colors.txt["secondary"] }}
                        />
                        <Box
                            sx={{
                                fontSize: 13,
                                fontFamily: "Roboto, sans-serif",
                                color: colors.txt["secondary"],
                            }}
                        >
                            {t("add_header_image") || "Voeg een header afbeelding toe"}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* BODY — Split layout, full height, like MCreateBatch body */}
            <Box
                sx={{
                    flex: 1,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "row",
                    overflow: "hidden",
                    transition: "250ms",
                }}
            >
                {/* Left: Article builder (title + sections) */}
                <Box
                    sx={{
                        flex: 2,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <ArticleBuilderView
                        title={title}
                        setTitle={setTitle}
                        sections={sections}
                        setSections={setSections}
                        colors={colors}
                        theme={theme}
                    />
                </Box>

                {/* Right: Audience panel — full height divider */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        borderLeft: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                        display: "flex",
                        flexDirection: "column",
                        "@media screen and (max-width: 47.9375em)": {
                            display: "none",
                        },
                    }}
                >
                    <AudiencePanelView
                        audience={audience}
                        setAudience={setAudience}
                        excludedCustomerIds={excludedCustomerIds}
                        setExcludedCustomerIds={setExcludedCustomerIds}
                        excludedRelationCustomerIds={excludedRelationCustomerIds}
                        setExcludedRelationCustomerIds={setExcludedRelationCustomerIds}
                        colors={colors}
                        theme={theme}
                    />
                </Box>
            </Box>

            {/* FOOTER */}
            <Box
                component="footer"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    paddingRight: 3,
                    paddingTop: 2,
                    zIndex: 1058,
                    position: "relative",
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    },
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>

                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={<SaveOutlinedIcon />}
                    >
                        {t("save")}
                    </Shbutton>
                    <Shbutton
                        className={"group-btn"}
                        color="green"
                        variant="contained"
                        onClick={handlePublish}
                        disabled={publishDisabled}
                        startIcon={<PublishIcon />}
                    >
                        {t("save_and_publish") || "Opslaan & publiceren"}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModalBottom>
    );
};

export default MCreateNewsArticle;
